import React, { useState, useEffect, useRef } from "react";
import {
  View,
  TouchableOpacity,
  Dimensions,
  StyleSheet,
  Platform,
  StatusBar,
  Image,
} from "react-native";
import { useTheme, ActivityIndicator } from "react-native-paper";
import { ThemeType } from "../../theme/theme";
import { useNavigation } from "@react-navigation/native";
import {
  updateFrontPhoto,
  updateBackPhoto,
  processFrontCheckScanImage,
  processBackCheckScanImage,
} from "../../redux/actions/actionCreators";
import { useDispatch, useSelector } from "react-redux";
import { Camera, CameraCapturedPicture } from "expo-camera";
import BarcodeMask from "react-native-barcode-mask";
import * as ImageManipulator from "expo-image-manipulator";
import { CheckImageType } from "../../enums/check";
import { Text } from "../../components/text/text";
import { fontSize } from "../../theme/fontSize";
import { CameraInfoView } from "../../components/check-scan/camera-info-view";
import { RootState } from "../../redux/reducers";
import automationTestingLocators from "./utils/automation-testing-locators.json";

const IMAGE_SIZE = 1024;
const ORIENTATION_NORMAL = 1;
const ORIENTATION_UNDEFINED = 0;
const ORIENTATION_ROTATE_90 = 6;
const ORIENTATION_ROTATE_180 = 3;
const ORIENTATION_ROTATE_270 = 8;
const CHECK_WIDTH = 7.5;
const CHECK_HEIGHT = 15.5;

export type CheckScanCameraParamsList = {
  checkImageType: CheckImageType;
};

export type CameraScreenProps = {
  route: { name: string; key: string; params: CheckScanCameraParamsList };
};

export const PaymentFlowPaymentMethodMobileCheckPaymentCamera = ({ route }: CameraScreenProps) => {
  const { params } = route;
  const { checkImageType } = params;
  const [hasPermission, setHasPermission] = useState(null);
  const cameraRef = useRef(null);
  const [flash, setFlash] = useState(Camera.Constants.FlashMode.off);
  const [capturing, setCapturing] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const theme = useTheme() as ThemeType;
  const { spacing } = theme;
  const { width, height } = Dimensions.get("window");
  const style = getStyles(theme, width, height);
  const logger = useSelector((state: RootState) => state.logger);
  useEffect(() => {
    (async () => {
      const { status } = await Camera.requestCameraPermissionsAsync();
      setHasPermission(status === "granted");
    })();
  }, []);

  useEffect(() => {
    if (hasPermission === false) {
      navigation.goBack();
    }
  }, [hasPermission]);

  /**
   * I assume that white angles' overlay should be a form of cheque.
   * So calculating, the overlay size base on assumption that the overlay should be between
   * buttons and it should have aspect ratio 15.5 : 7.5 (This is a width:height ration of a cheque)
   */
  const overlayWidthAndHeight = () => {
    var overlayHeight = height - 2 * (spacing.large + 2 * spacing.huge + spacing.smaller);
    var overlayWidth = width - 2 * (spacing.huge + spacing.medium);
    if (overlayHeight > (overlayWidth * CHECK_HEIGHT) / CHECK_WIDTH) {
      overlayHeight = (overlayWidth * CHECK_HEIGHT) / CHECK_WIDTH;
    } else if (overlayWidth > (overlayHeight * CHECK_WIDTH) / CHECK_HEIGHT) {
      overlayWidth = (overlayHeight * CHECK_WIDTH) / CHECK_HEIGHT;
    }
    return { overlayWidth, overlayHeight };
  };

  const { overlayWidth, overlayHeight } = overlayWidthAndHeight();

  /**
   * Fixing image orientation here.
   * @param photo image from camera in any orientation.
   */
  const fixOrientation = async (photo: CameraCapturedPicture) => {
    const { Orientation: orientation } = photo.exif;
    var degree = 0;
    if (Platform.OS === "android") {
      switch (orientation) {
        case ORIENTATION_NORMAL:
        case ORIENTATION_UNDEFINED:
          degree = -90;
          break;
        case ORIENTATION_ROTATE_90:
          degree = -180;
          break;
        case ORIENTATION_ROTATE_180:
          degree = -270;
          break;
        case ORIENTATION_ROTATE_270:
          degree = 0;
          break;
      }
    } else if (Platform.OS === "web") {
      degree = 0;
    } else {
      degree = orientation;
    }
    const result = await ImageManipulator.manipulateAsync(photo.uri, [{ rotate: degree }], {
      compress: 1,
      format: ImageManipulator.SaveFormat.JPEG,
    });
    return result;
  };

  /**
   * Since we are emulating landscape orientation with layout orientation
   * image is comming with correct (landscape) orientation, but screen has portrait orientation.
   * @param photo image from camera in landscape orientation
   */
  const cropCheck = async (photo: CameraCapturedPicture) => {
    const deltaWidth = photo.width / height;
    const deltaHeight = photo.height / width;
    const result = await ImageManipulator.manipulateAsync(
      photo.uri,
      [
        {
          crop: {
            originX: ((height - overlayHeight) / 2) * deltaWidth,
            originY: ((width - overlayWidth) / 2) * deltaHeight,
            width: overlayHeight * deltaWidth,
            height: overlayWidth * deltaHeight,
          },
        },
      ],
      { compress: 0.6, format: ImageManipulator.SaveFormat.JPEG }
    );
    return result;
  };

  const resizeCheck = async (photo: CameraCapturedPicture) => {
    var result = photo;
    if (photo.height > photo.width && photo.height > IMAGE_SIZE) {
      result = await ImageManipulator.manipulateAsync(photo.uri, [
        { resize: { height: IMAGE_SIZE } },
      ]);
    } else if (photo.width > photo.height && photo.width > IMAGE_SIZE) {
      result = await ImageManipulator.manipulateAsync(photo.uri, [
        { resize: { width: IMAGE_SIZE } },
      ]);
    }
    return result;
  };

  const takePhoto = async () => {
    if (cameraRef && cameraRef.current) {
      try {
        setCapturing(true);
        const photo = await cameraRef.current.takePictureAsync({ exif: true });
        const fixedOrientationPhoto = await fixOrientation(photo);
        const checkPhoto = await cropCheck(fixedOrientationPhoto);
        const resizedPhoto = await resizeCheck(checkPhoto);

        switch (checkImageType) {
          case CheckImageType.FRONT:
            dispatch(updateFrontPhoto(resizedPhoto.uri));
            dispatch(processFrontCheckScanImage(true));
            break;
          case CheckImageType.BACK:
            dispatch(updateBackPhoto(resizedPhoto.uri));
            dispatch(processBackCheckScanImage(true));
            break;
        }
        navigation.goBack();
      } catch (error) {
        // console.log(error);
        setCapturing(false);
      }
    }
  };

  const switchFlash = () => {
    setFlash(
      flash === Camera.Constants.FlashMode.on
        ? Camera.Constants.FlashMode.off
        : Camera.Constants.FlashMode.on
    );
  };

  const title =
    checkImageType === CheckImageType.FRONT
      ? "homeStack.frontOfCheckCamera"
      : "homeStack.backOfCheckCamera";

  if (hasPermission === null) {
    return <View />;
  }

  return showInfo ? (
    <CameraInfoView onClose={() => setShowInfo(false)} />
  ) : (
    <View style={style.full}>
      <StatusBar hidden={true} />
      <Camera
        style={style.full}
        type={Camera.Constants.Type.back}
        ref={cameraRef}
        flashMode={flash}
        autoFocus={Camera.Constants.AutoFocus.on}
        ratio={"16:9"}
        {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPayCamera.camera}>
        <BarcodeMask
          width={overlayWidth}
          height={overlayHeight}
          showAnimatedLine={false}
          outerMaskOpacity={0.0}
          edgeHeight={50}
          edgeWidth={50}
        />
        <View style={style.textContainer}>
          <Text
            style={style.text}
            tx={title}
            {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPayCamera.cameraTitle}
          />
        </View>
        {!capturing ? (
          <TouchableOpacity
            style={style.crossContainer}
            onPress={() => navigation.goBack()}
            {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPayCamera
              .crossIconButton}>
            <Image
              style={style.iconCross}
              source={require("../../../assets/icon-cross.png")}
              {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPayCamera
                .crossIconImage}
            />
          </TouchableOpacity>
        ) : null}
        {!capturing ? (
          <TouchableOpacity
            style={style.flashContainer}
            onPress={switchFlash}
            {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPayCamera
              .flashIconButton}>
            <Image
              style={style.iconFlash}
              source={
                flash === Camera.Constants.FlashMode.off
                  ? require("../../../assets/icon-flash-off.png")
                  : require("../../../assets/icon-flash-on.png")
              }
              {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPayCamera
                .flashIconImage}
            />
          </TouchableOpacity>
        ) : null}
        {!capturing ? (
          <TouchableOpacity
            style={style.questionContainer}
            onPress={() => setShowInfo(true)}
            {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPayCamera
              .questionMarkIconButton}>
            <Image
              style={style.iconQuestion}
              source={require("../../../assets/question-mark.png")}
              {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPayCamera
                .questionMarkIconImage}
            />
          </TouchableOpacity>
        ) : null}
        {!capturing ? (
          <TouchableOpacity
            style={style.captureContainer}
            onPress={takePhoto}
            disabled={capturing}
            {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPayCamera
              .cameraIconButton}>
            <Image
              style={style.iconPhotograph}
              source={require("../../../assets/icon-photograph.png")}
              {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPayCamera
                .cameraIconImage}
            />
          </TouchableOpacity>
        ) : (
          <ActivityIndicator
            style={style.captureContainer}
            color={theme.colors.white}
            animating={true}
            {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPayCamera
              .activityIndicator}
          />
        )}
      </Camera>
    </View>
  );
};

const getStyles = (theme: ThemeType, width: number, height: number) => {
  const { colors, spacing, iconSize } = theme;
  return StyleSheet.create({
    full: {
      flex: 1,
    },
    crossContainer: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      position: "absolute",
      right: spacing.medium,
      top: spacing.large,
      height: spacing.huge,
      width: spacing.huge,
      borderRadius: spacing.large,
      backgroundColor: colors.translucent,
      borderColor: colors.white,
      borderWidth: 1,
    },
    captureContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "absolute",
      bottom: spacing.large,
      left: (width - spacing.huge) / 2,
      height: spacing.massive,
      width: spacing.massive,
      borderRadius: spacing.larger,
      backgroundColor: colors.red,
      transform: [{ rotate: "90deg" }],
    },
    flashContainer: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      position: "absolute",
      bottom: spacing.large + spacing.huge + spacing.smaller,
      right: spacing.medium,
      transform: [{ rotate: "90deg" }],
      height: spacing.huge,
      width: spacing.huge,
      borderRadius: spacing.large,
      backgroundColor: colors.translucent,
      borderColor: colors.white,
      borderWidth: 1,
    },
    questionContainer: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      position: "absolute",
      bottom: spacing.large,
      right: spacing.medium,
      transform: [{ rotate: "90deg" }],
      height: spacing.huge,
      width: spacing.huge,
      borderRadius: spacing.large,
      backgroundColor: colors.translucent,
      borderColor: colors.white,
      borderWidth: 1,
    },
    textContainer: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      position: "absolute",
      right: -60,
      height: height,
      width: 200,
      transform: [{ rotate: "90deg" }],
    },
    text: {
      fontSize: fontSize.larger + fontSize.tiny,
      fontWeight: "500",
      textAlign: "center",
      color: colors.white,
    },
    iconQuestion: {
      height: iconSize.medium,
      width: iconSize.medium,
      resizeMode: "contain",
    },
    iconFlash: {
      height: iconSize.small,
      width: iconSize.small,
      resizeMode: "contain",
    },
    iconCross: {
      height: iconSize.smaller + iconSize.tiny,
      width: iconSize.smaller + iconSize.tiny,
      resizeMode: "contain",
    },
    iconPhotograph: {
      height: iconSize.small + iconSize.tiny,
      width: iconSize.small + iconSize.tiny,
      resizeMode: "contain",
    },
  });
};
